import { Box, Center, Flex, Heading, Stack, Text } from '@chakra-ui/react'
import { faBolt, faClock, faIdCard, faVideo } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import type { AgeAssuranceMethod } from '@/client/services/ageAssurance/types'

type MethodCardConfig = {
  icon: typeof faIdCard
  iconBgColor: string
  iconColor: string
  cardBgColor: string
  cardSelectedBorderColor: string
  title: string
  badgeLabel: string
  badgeIcon: typeof faBolt
  badgeBgColor?: string
  badgeColor: string
  description: string
}

const METHOD_CARD_CONFIGS: Record<AgeAssuranceMethod, MethodCardConfig> = {
  yoti: {
    icon: faIdCard,
    iconBgColor: 'primary.muted',
    iconColor: 'primary.fg',
    cardBgColor: 'primary.subtle',
    title: 'ID Verification',
    badgeLabel: 'Instant',
    badgeIcon: faBolt,
    badgeBgColor: 'primary.emphasized',
    badgeColor: 'primary.contrast',
    description:
      'Upload a photo of your government-issued ID. Verification is completed instantly.',
    cardSelectedBorderColor: 'primary.emphasized',
  },
  videoask: {
    icon: faVideo,
    iconBgColor: 'bg.muted',
    iconColor: 'fg.muted',
    cardBgColor: 'bg.subtle',
    cardSelectedBorderColor: 'border.emphasized',
    title: 'Manual Verification',
    badgeLabel: 'Up to 2 business days',
    badgeIcon: faClock,
    badgeBgColor: 'bg.muted',
    badgeColor: 'fg.muted',
    description:
      'Record and submit a short video. Our team will review it manually and verify you within 2 business days.',
  },
}

export default function AgeAssuranceMethodCard({
  method,
  isSelected = false,
  onClick,
}: {
  method: AgeAssuranceMethod
  isSelected?: boolean
  onClick?: () => void
}) {
  const config = METHOD_CARD_CONFIGS[method]

  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      cursor="pointer"
      borderRadius="12px"
      border="2px solid transparent"
      borderColor={isSelected ? config.cardSelectedBorderColor : 'transparent'}
      bg={config.cardBgColor}
      px="24px"
      py="20px"
      textAlign="left"
      transition="border-color 0.15s"
    >
      <Flex gap="16px" alignItems="flex-start">
        <Center
          width="48px"
          height="48px"
          minWidth="48px"
          bgColor={config.iconBgColor}
          borderRadius="8px"
        >
          <Text color={config.iconColor} fontSize="20px">
            <FontAwesomeIcon icon={config.icon} />
          </Text>
        </Center>
        <Stack gap="4px">
          <Flex alignItems="center" gap="8px" flexWrap="wrap">
            <Heading size="sm">{config.title}</Heading>
            <Flex
              alignItems="center"
              gap="4px"
              bgColor={config.badgeBgColor}
              color={config.badgeColor}
              borderRadius="full"
              px={config.badgeBgColor != null ? '8px' : undefined}
              py={config.badgeBgColor != null ? '2px' : undefined}
              fontSize="xs"
              fontWeight="bold"
            >
              <FontAwesomeIcon icon={config.badgeIcon} />
              {config.badgeLabel}
            </Flex>
          </Flex>
          <Text color="fg.muted" fontSize="sm" fontWeight="semibold">
            {config.description}
          </Text>
        </Stack>
      </Flex>
    </Box>
  )
}
